import React from "react";

const LAYERS = [
  {
    label: "Outer Shell",
    title: "400-Thread-Count 100% Egyptian Cotton",
    weight: "0.18 kg",
    weightLabel: "Shell weight",
    body:
      "Hand-loomed long-staple Egyptian cotton — the exact fabric used to dress beds at the Burj Al Arab and Ritz Paris. Breathes 3× better than synthetic blends, dissipates body heat in seconds, and feels cool against your face all night.",
    benefit: "You wake up dry. No tossing to find the cool spot.",
    icon: "🌾",
  },
  {
    label: "Hand-Stitched Quilting",
    title: "47-Chamber Pinch-Tuck Architecture",
    weight: "Triple-stitched",
    weightLabel: "Frame integrity",
    body:
      "Each of the 47 chambers is hand-sewn in Dubai with triple-reinforced seams. The pinch-tuck design locks air inside permanently — the down can't migrate or clump. This is what gives the pillow its 5-year structural memory.",
    benefit: "Keeps its loft for 5+ years. Not 5 months.",
    icon: "🪡",
  },
  {
    label: "Inner Fill",
    title: "100% European Goose Down · 800+ Fill Power",
    weight: "0.85 kg",
    weightLabel: "Down weight",
    body:
      "Hand-collected, RDS-certified Hungarian goose down — the highest fill-power grade available anywhere. Each cluster traps 7× more air than feather or polyester. Cradles your cervical spine without ever bottoming out.",
    benefit: "Cloud-soft and supportive in the same touch.",
    icon: "🪶",
  },
  {
    label: "Anti-Allergen Treatment",
    title: "OEKO-TEX® Sterilised at 90°C · Hypoallergenic",
    weight: "0 chemicals",
    weightLabel: "Purity",
    body:
      "Triple-washed with plant-based detergents and sterilised at 90 °C — zero dust mites, zero allergens, zero VOCs. Tested by independent European labs to OEKO-TEX® Standard 100 — safe for sensitive skin, asthma, eczema, and infants.",
    benefit: "Safe enough for the most allergic sleeper.",
    icon: "🌿",
  },
];

const STATS = [
  { value: "1.03 kg", label: "Total weight" },
  { value: "74 × 48 cm", label: "Hotel size" },
  { value: "800+", label: "Fill power" },
  { value: "5 + years", label: "Structural memory" },
];

export default function Composition() {
  return (
    <section className="aurora-wrap py-20 md:py-28" data-testid="composition">
      <div className="container-x">
        <div className="text-center max-w-3xl mx-auto">
          <span className="section-pill">🔬 The architecture inside</span>
          <h2 className="mt-5 text-[34px] sm:text-[44px] md:text-[56px] leading-[1.05] font-black tracking-[-0.02em] text-[#0e0e10]">
            Hand-built like furniture.
            <br />
            <span className="text-[#8a8a8a]">Engineered like a watch.</span>
          </h2>
          <p className="mt-5 text-[#5a5a60] text-[15px] md:text-[16px]">
            Most pillows are a bag of fluff sewn shut. The Dubai Pillow is an architecture —
            four certified layers, 47 hand-stitched chambers, 1.03 kg of weight you can feel
            the moment you pick it up.
          </p>
        </div>

        {/* HERO STAT STRIP — gives the pillow physical "weight" in the user's mind */}
        <div className="mt-12 max-w-4xl mx-auto bg-white border border-black/5 rounded-3xl shadow-sm p-5 md:p-7">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-black/5">
            {STATS.map((s) => (
              <div key={s.label} className="px-3 md:px-5 text-center">
                <div className="text-[22px] md:text-[28px] font-black text-[#0e0e10] tabular-nums leading-none">
                  {s.value}
                </div>
                <div className="mt-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-[#8a8a8a] font-bold">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 4 LAYER CARDS — each now exposes weight + benefit, not just spec */}
        <div className="mt-10 max-w-5xl mx-auto grid md:grid-cols-2 gap-5 md:gap-7">
          {LAYERS.map((l, i) => (
            <div
              key={l.title}
              data-testid={`layer-${i + 1}`}
              className="bg-white rounded-3xl border border-black/5 p-7 md:p-8 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-3 mb-4">
                <span className="w-12 h-12 rounded-2xl bg-[#faf8f4] flex items-center justify-center text-2xl shrink-0">
                  {l.icon}
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#b8952a] font-bold">
                    Layer {String(i + 1).padStart(2, "0")} · {l.label}
                  </div>
                  <h3 className="font-extrabold text-[16px] md:text-[18px] text-[#0e0e10] leading-tight">
                    {l.title}
                  </h3>
                </div>
              </div>

              <p className="text-[14px] md:text-[15px] text-[#5a5a60] leading-relaxed">
                {l.body}
              </p>

              <div className="mt-5 pt-4 border-t border-dashed border-black/10 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[11px] text-[#8a8a8a] uppercase tracking-wider">
                  <span className="font-bold text-[#0e0e10] normal-case">{l.weight}</span>{" "}
                  · {l.weightLabel}
                </div>
                <div className="text-[12px] font-extrabold text-[#22c55e] inline-flex items-center gap-1.5">
                  → {l.benefit}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="mt-10 flex flex-wrap justify-center gap-3 text-[11px]">
          {["OEKO-TEX® Certified", "RDS Certified Down", "GOTS Organic Cotton", "Hand-Made in Dubai", "Hypoallergenic"].map(
            (b) => (
              <span key={b} className="section-pill !text-[#0e0e10] font-semibold">
                ✓ {b}
              </span>
            )
          )}
        </div>
      </div>
    </section>
  );
}
